import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/utils";
import { floatingContentZClass } from "@/lib/native/nativeZIndex";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { NativeOptionSheet } from "@/components/native/NativeOptionSheet";

function getSelectItemLabel(children: React.ReactNode): string {
  if (children == null || typeof children === "boolean") return "";
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(getSelectItemLabel).join("");
  }
  if (React.isValidElement(children)) {
    return getSelectItemLabel(children.props.children);
  }
  return "";
}

type NativeSelectOption = { value: string; label: string };

type SelectNativeContextValue = {
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  options: NativeSelectOption[];
  registerOption: (opt: NativeSelectOption) => void;
  sheetTitle: string;
  setSheetTitle: (title: string) => void;
};

const SelectNativeContext = React.createContext<SelectNativeContextValue | null>(
  null,
);

type SelectProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
  /** Bottom sheet title on native (defaults to "Välj"). */
  nativeSheetTitle?: string;
};

const Select = ({
  children,
  nativeSheetTitle,
  ...props
}: SelectProps) => {
  const { isNative } = useNativePlatform();

  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<NativeSelectOption[]>([]);
  const [sheetTitle, setSheetTitle] = React.useState(nativeSheetTitle ?? "Välj");

  React.useEffect(() => {
    if (nativeSheetTitle) setSheetTitle(nativeSheetTitle);
  }, [nativeSheetTitle]);

  const registerOption = React.useCallback((opt: NativeSelectOption) => {
    setOptions((prev) => {
      const idx = prev.findIndex((o) => o.value === opt.value);
      if (idx === -1) return [...prev, opt];
      const next = [...prev];
      next[idx] = opt;
      return next;
    });
  }, []);

  React.useEffect(() => {
    return () => setOptions([]);
  }, []);

  if (!isNative) {
    return <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>;
  }

  const ctx: SelectNativeContextValue = {
    value: props.value,
    onValueChange: props.onValueChange,
    disabled: props.disabled,
    open,
    setOpen,
    options,
    registerOption,
    sheetTitle,
    setSheetTitle,
  };

  return (
    <SelectNativeContext.Provider value={ctx}>
      {children}
      <NativeOptionSheet
        open={open}
        onOpenChange={setOpen}
        title={sheetTitle}
        value={props.value ?? ""}
        options={options}
        nestedSheet
        onSelect={(v) => {
          props.onValueChange?.(v);
          setOpen(false);
        }}
      />
    </SelectNativeContext.Provider>
  );
};

const SelectGroup = SelectPrimitive.Group;

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>((props, ref) => {
  const native = React.useContext(SelectNativeContext);
  if (native) return null;
  return <SelectPrimitive.Value ref={ref} {...props} />;
});
SelectValue.displayName = SelectPrimitive.Value.displayName;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const native = React.useContext(SelectNativeContext);

  React.useEffect(() => {
    if (!native) return;
    const aria = props["aria-label"];
    if (typeof aria === "string" && aria.trim()) {
      native.setSheetTitle(aria);
    }
  }, [native, props["aria-label"]]);

  if (native) {
    const selected = native.options.find((o) => o.value === native.value);
    return (
      <button
        type="button"
        ref={ref as React.Ref<HTMLButtonElement>}
        disabled={native.disabled}
        onClick={() => !native.disabled && native.setOpen(true)}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      >
        <span className="truncate text-left">
          {selected?.label ?? native.value ?? children}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-primary" />
      </button>
    );
  }

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 text-primary" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
  const native = React.useContext(SelectNativeContext);
  if (native) {
    return <>{children}</>;
  }

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          floatingContentZClass(),
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => {
  const native = React.useContext(SelectNativeContext);
  if (native) return null;
  return (
    <SelectPrimitive.Label
      ref={ref}
      className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
      {...props}
    />
  );
});
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, value, disabled, ...props }, ref) => {
  const native = React.useContext(SelectNativeContext);
  const label = getSelectItemLabel(children) || value;

  React.useEffect(() => {
    if (!native || disabled) return;
    native.registerOption({ value, label });
  }, [native, value, label, disabled]);

  if (native) return null;

  return (
    <SelectPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent/20 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => {
  const native = React.useContext(SelectNativeContext);
  if (native) return null;
  return (
    <SelectPrimitive.Separator
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-muted", className)}
      {...props}
    />
  );
});
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
